import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Clapperboard } from "lucide-react";

export default function AuthPage() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, login, register } = useAuth();
  const nav = useNavigate();

  useEffect(() => { if (user && user !== false) nav("/studio"); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const cleanEmail = email.trim().toLowerCase();
    try {
      if (mode === "register") await register(cleanEmail, password, name);
      else await login(cleanEmail, password);
      nav("/studio");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-5 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md glass rounded-3xl p-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-xl bg-primary/15 grid place-items-center glow-cyan">
            <Clapperboard className="w-5 h-5 text-primary" />
          </div>
          <span className="font-display font-extrabold text-xl">MotionCraft</span>
        </div>
        <h1 className="font-display text-3xl font-bold mb-1">
          {mode === "register" ? "Create your studio" : "Welcome back"}
        </h1>
        <p className="text-muted-foreground text-sm mb-6">
          {mode === "register" ? "Start turning ideas into motion." : "Log in to keep creating."}
        </p>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <Label className="text-xs mb-1.5 block">Name</Label>
              <Input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ava Director" className="rounded-xl bg-secondary/40" />
            </div>
          )}
          <div>
            <Label className="text-xs mb-1.5 block">Email</Label>
            <Input data-testid="auth-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@studio.com" autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email" className="rounded-xl bg-secondary/40" />
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">Password</Label>
            <Input data-testid="auth-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="rounded-xl bg-secondary/40" />
          </div>
          {error && <p data-testid="auth-error" className="text-sm text-destructive">{error}</p>}
          <Button data-testid="auth-submit" type="submit" disabled={loading} className="w-full rounded-xl font-semibold h-11">
            {loading ? "Please wait…" : mode === "register" ? "Create account" : "Log in"}
          </Button>
        </form>

        <p className="text-sm text-muted-foreground mt-5 text-center">
          {mode === "register" ? "Already have an account?" : "New here?"}{" "}
          <button data-testid="auth-toggle" onClick={() => { setMode(mode === "register" ? "login" : "register"); setError(""); }} className="text-primary font-semibold hover:underline">
            {mode === "register" ? "Log in" : "Create one"}
          </button>
        </p>
      </motion.div>
    </div>
  );
}
